import {
    DefaultPillFormatter,
    ExtendedColumn,
    HoveroverButton,
    NonSelectableRow,
} from '@sprint/sprint-react-components';
import React, { FunctionComponent, useContext } from 'react';
import { DealLostReasonRequest } from '../../Api/DealLostReasonRequest';
import UniqueKeyBuilder from '../../HelperFunctions/UniqueKeyBuilder';
import DealLostReason from '../../Models/DealLostReason';
import DealLostReasonEditState from '../../Models/DealLostReasonEditState';
import { UniqueKeyType } from '../../Models/Enums';
import { RepositoryFactoryContext } from '../../index';
import CampusDataGrid, { ActionBarMeta, AddSispMeta, DataGridMeta, PromptMeta } from '../CampusDataGrid';
import DealLostReasonsAddSisp from './DealLostReasonsAddSisp';
import DealLostReasonsEditSisp from './DealLostReasonsEditSisp';

interface Props {
    searchFilterPlaceholder: string;
    dataGridUniqueKey: string;
    dataGridEntitySingular: string;
    dataGridEntityPlural: string;
}

enum DealLostReasonsColumnKey {
    ID,
    LAST_MODIFIED,
    REASON,
}

const DealLostReasonsTable: FunctionComponent<Props> = (props: Props) => {
    const editSispUniqueKey = UniqueKeyBuilder.make(props.dataGridUniqueKey, UniqueKeyType.EDIT_SISP);

    const dealLostReasonsRepository = useContext(RepositoryFactoryContext).getApiRepository(
        new DealLostReasonRequest(),
    );
    const Columns: Record<DealLostReasonsColumnKey, ExtendedColumn> = {
        [DealLostReasonsColumnKey.ID]: {
            key: 'id',
            name: 'ID',
            sortable: false,
        },
        [DealLostReasonsColumnKey.LAST_MODIFIED]: {
            key: 'modified',
            name: 'Last Modified',
            sortable: true,
        },
        [DealLostReasonsColumnKey.REASON]: {
            key: 'reason',
            name: 'Name',
            sortable: true,
            resizable: false,
            renderCell: (fprops) => {
                const name = (fprops.row as DealLostReason).reason;
                const is_default = (fprops.row as DealLostReason).is_default;
                const built_in = (fprops.row as DealLostReason).built_in;
                const id = (fprops.row as DealLostReason).id!;
                const eventBusMessage: DealLostReasonEditState = { id: id, reason: name, is_default: is_default };
                return (
                    <>
                        <DefaultPillFormatter cellContent={name} showPill={is_default}>
                            <HoveroverButton
                                contents="Edit"
                                showHoverover={!built_in}
                                eventBusMessageTarget={editSispUniqueKey}
                                eventBusMessage={eventBusMessage}
                            />
                        </DefaultPillFormatter>
                    </>
                );
            },
        },
    };

    const DefaultColumns: ExtendedColumn[] = [Columns[DealLostReasonsColumnKey.REASON]];

    const findNonSelectableRows = (rows: any): NonSelectableRow[] | null => {
        return rows
            .filter((row: any) => (row as DealLostReason).built_in)
            .map((row: any) => ({ id: row.id, reason: 'Built-in Type' }));
    };

    const dataGridMeta: DataGridMeta = {
        uniqueKey: props.dataGridUniqueKey,
        entitySingular: props.dataGridEntitySingular,
        entityPlural: props.dataGridEntityPlural,
        columnOptions: Columns,
        defaultColumns: DefaultColumns,
        frozenColumns: [],
        defaultSortColumn: 'modified',
        nonSelectableRows: findNonSelectableRows,
    };

    const actionBarMeta: ActionBarMeta = {
        searchPlaceHolder: props.searchFilterPlaceholder,
        includeCounts: true,
    };

    const addSispMeta: AddSispMeta = {
        key: UniqueKeyBuilder.make(props.dataGridUniqueKey, UniqueKeyType.ADD_SISP),
        sisp: DealLostReasonsAddSisp,
    };
    const promptMeta: PromptMeta = {
        icon: '/assets/application/img/prompts/deal_lost_reasons.svg',
        iconHeight: 90,
    };

    return (
        <>
            <CampusDataGrid
                repository={dealLostReasonsRepository}
                actionBarMeta={actionBarMeta}
                addSispMeta={addSispMeta}
                editSispMeta={{ sisp: DealLostReasonsEditSisp }}
                dataGridMeta={dataGridMeta}
                promptMeta={promptMeta}
            />
        </>
    );
};

export default DealLostReasonsTable;
